import { map, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { Blocs } from '@business/blocs';
import { LoadingState } from '@core/utils/repository/loading_state';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { R } from '@core/utils/r';
import { CommonService } from '@business/services';
import { ChecklistId } from '@business/entities/checklist';
import {
  FetchTaskCountResponse,
  TaskId,
  TaskResponse,
  TasksMetaResponse,
  TaskTextSearchResponse,
  TodoTasksRequest,
  TodoTaskStatus,
} from '@business/entities/task';
import { TaskPriority } from '@business/constants/task';
import { TaskAPI } from '@business/api/task';

export class TodosPageBloc extends BaseBloc {
  private readonly userBloc = resolve(Blocs.user);
  private readonly todoBloc = resolve(Blocs.todo);
  private readonly taskBloc = resolve(Blocs.task);
  private readonly orgBloc = resolve(Blocs.organization);
  private readonly checklistBloc = resolve(Blocs.todoChecklists);
  private readonly teamBloc = resolve(Blocs.team);
  private readonly taskStatusBloc = resolve(Blocs.taskStatus);

  constructor(private readonly checklistId?: ChecklistId, private readonly status?: TodoTaskStatus) {
    super();
  }

  readonly loading = new LoadingState({ loading: false });

  readonly users$ = this.userBloc.staff$;
  // readonly tasks$ = this.todoBloc.tasks$.pipe(map(items => R.sortBy(items, item => item.menuOrder)));
  readonly checklists$ = this.checklistBloc.checklists$;
  //   readonly checklists$ = this.checklistBloc.checklists$.pipe(
  //     map(items => {
  //       R.filter(items, c => {
  //         if (c.recurring === 'one-time') {
  //           return true;
  //         } else {
  //           return !c?.isParent;
  //         }
  //       });
  //     }),
  //   );
  readonly taskStatuses$ = this.taskStatusBloc.statuses$;
  //   readonly tasks$ = new BehaviorSubject<TaskResponse[]>([]);
  //   readonly taskPagination$ = new BehaviorSubject<TasksMetaResponse | null>(null);
  readonly tasks$ = this.todoBloc.tasksWithPagination$;
  readonly taskPagination$ = this.todoBloc.taskPagination$;
  readonly taskCount$ = new BehaviorSubject<FetchTaskCountResponse>({ pending: 0, completed: 0 });

  // Text search state
  readonly searchResults$ = new BehaviorSubject<TaskTextSearchResponse | null>(null);
  readonly isSearching$ = new BehaviorSubject<boolean>(false);
  readonly searchQuery$ = new BehaviorSubject<string>('');

  teams$ = this.teamBloc.teams$;
  allUsers$ = this.userBloc.staff$;

  onInit() {
    return forkJoin([
      this.teamBloc.fetchTeams(),
      this.userBloc.fetchUsers(),
      // this.todoBloc.fetchTasks(),
      this.taskStatusBloc.fetchTaskStatuses(),
      this.checklistId ? this.checklistBloc.fetchChecklists(this.checklistId, { status: this.status }) : of(null),
      //   this.checklistBloc.fetchChecklists(this.checklistId, { status: this.status }),
    ]).pipe(this.loading.run());
  }

  onReload() {
    this.taskBloc.fetchTasks().subscribe();
  }

  onTodoReload(params?: TodoTasksRequest) {
    // this.todoBloc.fetchTasks().subscribe();
    this.todoBloc
      .fetchTodoTasksWithPagination({ ...params })
      .pipe(
        // tap(data => {
        //   this.tasks$.next(data.data);
        //   this.taskPagination$.next(data.meta);
        // }),
        this.loading.run(),
      )
      .subscribe();

    this.todoBloc
      .fetchTaskCount({ status: params.status, id: params?.id })
      .pipe(
        tap(data => {
          this.taskCount$.next(data);
        }),
      )
      .subscribe();
  }

  createTask = (task: Partial<TaskResponse>, parentId?: TaskId) => {
    return this.todoBloc.createTask(task, parentId).pipe(
      tap(item => {
        this.tasks$.next([...this.tasks$.value, item]);
      }),
    );
  };
  updateTask = this.todoBloc.updateTask;
  orderTasks = this.todoBloc.orderTasks;
  deleteTask = (id: string) => {
    return this.todoBloc.deleteTask(id).pipe(
      tap(() => {
        this.tasks$.next(this.tasks$.value.filter(item => item.id !== id));
      }),
    );
  };
  // setComplete = this.todoBloc.setComplete;
  setComplete(id: TaskId, isCompleted: boolean) {
    return this.taskBloc.setComplete(id, isCompleted).pipe(
      tap(item => {
        this.tasks$.next(this.tasks$.value.map(task => (task.id === id ? item : task)));
      }),
    );
  }
  setChecklistComplete = this.checklistBloc.toggleStep;
  search = this.checklistBloc.search;
  fetchChecklists = (searchId: string, status: TodoTaskStatus = TodoTaskStatus?.pending) =>
    this.checklistBloc.fetchChecklists(this.checklistId, { status, searchId });
  searchTask = this.todoBloc.search;
  updateAssignne = this.checklistBloc.updateAssignne;

  searchOrganizations = (search: string) => {
    return this.orgBloc.searchOrganizations(search).pipe(map(data => CommonService.selectItems(data.data, 'name', 'id')));
  };

  // Text search methods
  performTextSearch = (params: TodoTasksRequest) => {
    if (!params?.search || !params?.search?.trim()) {
      this.clearSearch();
      return;
    }

    this.isSearching$.next(true);
    this.searchQuery$.next(params.search);

    TaskAPI.textSearch(params.search, params)
      .pipe(
        tap(results => {
          this.searchResults$.next(results);
          this.isSearching$.next(false);
        }),
      )
      .subscribe({
        error: () => {
          this.isSearching$.next(false);
        },
      });
  };

  clearSearch = () => {
    this.searchQuery$.next('');
    this.searchResults$.next(null);
    this.isSearching$.next(false);
  };
}
